import {
  ResponseResult,
  type SubQuestionResponseEntry,
} from '@amzn/gbsa-helios-typescript-client';

/**
 * Derives the parent question's aggregated `responseResult` from
 * the current set of sub-question answers.
 *
 * FAIL is dominant and can be reported as soon as any answered
 * child fails, even while other children are missing or unanswered.
 * Without a FAIL, the parent stays unanswered until every defined
 * child is represented and answered. A complete parent is PASS when
 * any child passes; otherwise every child is NA and the parent is NA.
 *
 *   3 subs, PASS + (unanswered) + FAIL → FAIL
 *   3 subs, PASS + NA + (unanswered) → undefined
 *   3 subs, PASS + PASS + NA → PASS
 *   3 subs, all NA → NA
 *   0 subs → undefined (defensive; caller shouldn't invoke here)
 *
 * Pure — no react, no side effects. Consumed by optimistic audit
 * cache patches so parent result badges and metrics update on tap.
 */
export function deriveParentResult(
  subQuestionResponses: readonly SubQuestionResponseEntry[] | undefined,
  totalSubQuestions: number,
): ResponseResult | undefined {
  if (totalSubQuestions <= 0) return undefined;

  for (const entry of subQuestionResponses ?? []) {
    if (entry.response?.responseResult === ResponseResult.FAIL) {
      return ResponseResult.FAIL;
    }
  }

  if (!subQuestionResponses || subQuestionResponses.length < totalSubQuestions) {
    return undefined;
  }

  let hasPass = false;
  for (const entry of subQuestionResponses) {
    const result = entry.response?.responseResult;
    if (result === undefined) return undefined;
    if (result === ResponseResult.PASS) hasPass = true;
  }
  return hasPass ? ResponseResult.PASS : ResponseResult.NA;
}

/**
 * Derives whether the parent question is COMPLETED: every child
 * sub-question has an answer. Result precedence does not affect
 * completion; partial parents remain incomplete even when an answered
 * FAIL child already determines the aggregate result.
 *
 *   3 subs, all PASS → true
 *   3 subs, PASS + PASS + FAIL → true
 *   3 subs, PASS + PASS + (unanswered) → false
 *   3 subs, no responses cached → false
 *   0 subs → false (caller shouldn't invoke here)
 *
 * Pure — no react, no side effects. Drives the
 * `audit.metrics.completedQuestions` counter and sticky-nav progress.
 */
export function deriveParentCompletion(
  subQuestionResponses: readonly SubQuestionResponseEntry[] | undefined,
  totalSubQuestions: number,
): boolean {
  if (totalSubQuestions <= 0) return false;
  if (!subQuestionResponses || subQuestionResponses.length < totalSubQuestions) {
    return false;
  }
  return subQuestionResponses.every(
    (entry) => entry.response?.responseResult !== undefined,
  );
}
